import { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { Pencil, Plus, Trash2, X, Eye, EyeOff, ExternalLink } from "lucide-react";
import { API, authHeaders, formatApiErrorDetail } from "./api";
import { AField, ATextArea, ASelect, ImageUpload, SectionShell, SaveButton, Notice } from "./ui";

const CATEGORIES = ["HMO", "Stamp Duty", "Leasehold", "Investors", "General"];

const EMPTY_POST = {
  title: "",
  slug: "",
  excerpt: "",
  body: "",
  category: "General",
  cover_image: "",
  author: "Elin's Consultancy",
  published: true,
};

const slugify = (s) =>
  (s || "")
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "")
    .slice(0, 120);

const fmtDate = (iso) => {
  if (!iso) return "—";
  try {
    const d = new Date(iso);
    return `${String(d.getDate()).padStart(2, "0")}/${String(d.getMonth() + 1).padStart(2, "0")}/${d.getFullYear()}`;
  } catch {
    return "—";
  }
};

export const InsightsSection = () => {
  const [posts, setPosts] = useState(null);
  const [editing, setEditing] = useState(null); // null | "new" | post object
  const [form, setForm] = useState(EMPTY_POST);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [slugTouched, setSlugTouched] = useState(false);

  const load = async () => {
    try {
      const { data } = await axios.get(`${API}/admin/posts`, authHeaders());
      setPosts(data || []);
    } catch (e) {
      setError(formatApiErrorDetail(e.response?.data?.detail) || "Could not load posts");
    }
  };

  useEffect(() => {
    load();
  }, []);

  const openNew = () => {
    setForm(EMPTY_POST);
    setEditing("new");
    setSlugTouched(false);
    setError("");
    setSuccess("");
  };

  const openEdit = (post) => {
    setForm({ ...EMPTY_POST, ...post });
    setEditing(post);
    setSlugTouched(true);
    setError("");
    setSuccess("");
  };

  const closeEditor = () => {
    setEditing(null);
    setForm(EMPTY_POST);
    setSlugTouched(false);
  };

  const set = (k) => (v) =>
    setForm((f) => ({
      ...f,
      [k]: typeof v === "object" && v?.target ? v.target.value : v,
    }));

  const onTitle = (e) => {
    const v = e.target.value;
    setForm((f) => ({
      ...f,
      title: v,
      slug: slugTouched ? f.slug : slugify(v),
    }));
  };

  const onSlug = (e) => {
    setSlugTouched(true);
    setForm((f) => ({ ...f, slug: slugify(e.target.value) }));
  };

  const save = async (e) => {
    e.preventDefault();
    setError("");
    setSuccess("");
    setSaving(true);
    try {
      const payload = {
        title: form.title.trim(),
        slug: (form.slug || "").trim(),
        excerpt: form.excerpt || "",
        body: form.body,
        category: form.category || "General",
        cover_image: form.cover_image || "",
        author: form.author || "Elin's Consultancy",
        published: !!form.published,
      };
      if (editing === "new") {
        await axios.post(`${API}/admin/posts`, payload, authHeaders());
        setSuccess("Article published — live now.");
      } else {
        await axios.put(`${API}/admin/posts/${editing.id}`, payload, authHeaders());
        setSuccess("Article saved — live now.");
      }
      await load();
      closeEditor();
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || "Could not save article");
    } finally {
      setSaving(false);
    }
  };

  const remove = async (post) => {
    if (!window.confirm(`Delete "${post.title}"? This cannot be undone.`)) return;
    try {
      await axios.delete(`${API}/admin/posts/${post.id}`, authHeaders());
      await load();
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || "Could not delete");
    }
  };

  const sorted = useMemo(
    () =>
      (posts || [])
        .slice()
        .sort(
          (a, b) =>
            new Date(b.published_at || b.created_at) -
            new Date(a.published_at || a.created_at)
        ),
    [posts]
  );

  return (
    <SectionShell
      title="Insights"
      subtitle="Short editorial pieces on HMO licensing, Stamp Duty and leasehold. Each post goes live on /insights the moment you save."
      testid="admin-insights-section"
      action={
        !editing && (
          <button
            type="button"
            data-testid="insights-new-button"
            onClick={openNew}
            className="btn-gold"
          >
            <Plus size={14} strokeWidth={2.2} /> New Article
          </button>
        )
      }
    >
      <Notice error={error} success={success} />

      {!editing && (
        <>
          {posts === null && <p className="text-sm text-sage">Loading…</p>}
          {posts && posts.length === 0 && (
            <p className="text-sm text-sage bg-white border border-forest/10 px-6 py-10 text-center">
              No articles yet — publish your first piece.
            </p>
          )}
          {posts && posts.length > 0 && (
            <>
            <div className="hidden md:block bg-white border border-forest/10">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-forest/10 text-[10.5px] tracking-[0.18em] uppercase text-forest/60">
                    <th className="text-left px-5 py-3 font-bold">Title</th>
                    <th className="text-left px-4 py-3 font-bold">Category</th>
                    <th className="text-left px-4 py-3 font-bold">Published</th>
                    <th className="text-left px-4 py-3 font-bold">Status</th>
                    <th className="w-28 px-4 py-3" />
                  </tr>
                </thead>
                <tbody>
                  {sorted.map((p, i) => (
                    <tr
                      key={p.id}
                      data-testid={`insights-row-${i + 1}`}
                      className="border-b border-forest/5 last:border-b-0 hover:bg-cream-3/50 transition-colors"
                    >
                      <td className="px-5 py-4">
                        <p className="font-semibold text-forest">{p.title}</p>
                        <p className="text-[11.5px] text-sage mt-0.5 font-mono">
                          /insights/{p.slug}
                        </p>
                      </td>
                      <td className="px-4 py-4 text-[12px] uppercase tracking-[0.14em] text-gold-dark font-bold">
                        {p.category || "General"}
                      </td>
                      <td className="px-4 py-4 text-[13px] text-sage">
                        {fmtDate(p.published_at || p.created_at)}
                      </td>
                      <td className="px-4 py-4">
                        {p.published ? (
                          <span className="inline-flex items-center gap-1.5 text-[11px] font-bold text-forest">
                            <Eye size={13} strokeWidth={1.8} /> Live
                          </span>
                        ) : (
                          <span className="inline-flex items-center gap-1.5 text-[11px] font-bold text-sage">
                            <EyeOff size={13} strokeWidth={1.8} /> Draft
                          </span>
                        )}
                      </td>
                      <td className="px-4 py-4">
                        <div className="flex justify-end gap-1">
                          {p.published && (
                            <a
                              href={`/insights/${p.slug}`}
                              target="_blank"
                              rel="noreferrer"
                              className="p-2.5 text-forest/70 hover:text-forest transition-colors"
                              title="View on site"
                              data-testid={`insights-view-${i + 1}`}
                            >
                              <ExternalLink size={16} strokeWidth={1.6} />
                            </a>
                          )}
                          <button
                            type="button"
                            onClick={() => openEdit(p)}
                            data-testid={`insights-edit-${i + 1}`}
                            className="p-2.5 text-forest/70 hover:text-forest transition-colors"
                            title="Edit"
                          >
                            <Pencil size={16} strokeWidth={1.6} />
                          </button>
                          <button
                            type="button"
                            onClick={() => remove(p)}
                            data-testid={`insights-delete-${i + 1}`}
                            className="p-2.5 text-forest/60 hover:text-red-600 transition-colors"
                            title="Delete"
                          >
                            <Trash2 size={16} strokeWidth={1.6} />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            {/* Mobile cards */}
            <div className="md:hidden space-y-3">
              {sorted.map((p, i) => (
                <div key={p.id} data-testid={`insights-card-${i + 1}`} className="bg-white border border-forest/10 p-4">
                  <div className="flex items-start justify-between gap-3">
                    <div className="min-w-0 flex-1">
                      <p className="font-semibold text-forest">{p.title}</p>
                      <p className="text-[11.5px] text-sage mt-0.5 font-mono truncate">/insights/{p.slug}</p>
                      <div className="flex flex-wrap items-center gap-3 mt-2">
                        <span className="text-[11px] uppercase tracking-[0.14em] text-gold-dark font-bold">{p.category || "General"}</span>
                        <span className="text-[11.5px] text-sage">{fmtDate(p.published_at || p.created_at)}</span>
                        {p.published ? (
                          <span className="inline-flex items-center gap-1.5 text-[11px] font-bold text-forest"><Eye size={13} strokeWidth={1.8} /> Live</span>
                        ) : (
                          <span className="inline-flex items-center gap-1.5 text-[11px] font-bold text-sage"><EyeOff size={13} strokeWidth={1.8} /> Draft</span>
                        )}
                      </div>
                    </div>
                  </div>
                  <div className="flex gap-1 mt-3 pt-3 border-t border-forest/8 justify-end">
                    {p.published && (
                      <a href={`/insights/${p.slug}`} target="_blank" rel="noreferrer" className="p-2 text-forest/70 hover:text-forest" title="View on site"><ExternalLink size={16} strokeWidth={1.6} /></a>
                    )}
                    <button type="button" onClick={() => openEdit(p)} className="p-2 text-forest/70 hover:text-forest" title="Edit"><Pencil size={16} strokeWidth={1.6} /></button>
                    <button type="button" onClick={() => remove(p)} className="p-2 text-forest/60 hover:text-red-600" title="Delete"><Trash2 size={16} strokeWidth={1.6} /></button>
                  </div>
                </div>
              ))}
            </div>
            </>
          )}
        </>
      )}

      {editing && (
        <form
          onSubmit={save}
          className="bg-white border border-forest/10 p-6 sm:p-8 space-y-5 max-w-4xl"
          data-testid="insights-form"
        >
          <div className="flex items-center justify-between">
            <h3 className="font-serif text-xl text-forest">
              {editing === "new" ? "New Article" : "Edit Article"}
            </h3>
            <button
              type="button"
              onClick={closeEditor}
              className="p-2 text-forest/60 hover:text-forest"
              data-testid="insights-close-form"
              aria-label="Close editor"
            >
              <X size={18} strokeWidth={1.8} />
            </button>
          </div>

          <AField
            label="Title"
            testid="insights-field-title"
            value={form.title}
            onChange={onTitle}
            maxLength={180}
            required
          />
          <div className="grid grid-cols-1 sm:grid-cols-[1.4fr_1fr] gap-5">
            <AField
              label="URL slug"
              testid="insights-field-slug"
              value={form.slug}
              onChange={onSlug}
              placeholder="auto-generated-from-title"
              hint="Appears in the article URL: /insights/<slug>. Lowercase, dashes only."
            />
            <ASelect
              label="Category"
              testid="insights-field-category"
              value={form.category}
              onChange={set("category")}
              options={CATEGORIES}
            />
          </div>
          <ATextArea
            label="Excerpt (shown in cards and meta description)"
            testid="insights-field-excerpt"
            value={form.excerpt}
            onChange={set("excerpt")}
            rows={3}
            maxLength={320}
          />
          <ATextArea
            label="Body (Markdown — ## for subheadings, **bold**, *italic*, - for lists, [text](https://url))"
            testid="insights-field-body"
            value={form.body}
            onChange={set("body")}
            rows={16}
            required
          />
          <ImageUpload
            label="Cover image (optional)"
            testid="insights-field-cover"
            value={form.cover_image}
            onChange={set("cover_image")}
            hint="A landscape image works best — at least 1600px wide."
          />
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
            <AField
              label="Author byline"
              testid="insights-field-author"
              value={form.author}
              onChange={set("author")}
              placeholder="Elin's Consultancy"
            />
            <label className="flex items-center gap-3 pt-7">
              <input
                type="checkbox"
                data-testid="insights-field-published"
                checked={!!form.published}
                onChange={(e) => setForm((f) => ({ ...f, published: e.target.checked }))}
                className="h-5 w-5 accent-forest"
              />
              <span className="text-[13px] font-semibold text-forest">
                Publish immediately
              </span>
            </label>
          </div>

          <div className="flex gap-3">
            <SaveButton saving={saving} testid="insights-save-button" />
            <button
              type="button"
              onClick={closeEditor}
              className="btn-ghost-dark"
              data-testid="insights-cancel-button"
            >
              Cancel
            </button>
          </div>
        </form>
      )}
    </SectionShell>
  );
};
